import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, Save, X, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = ['Cutting', 'Forging', 'Machining', 'HT', 'FG'];

export default function PartTraceability() {
  const [items, setItems] = useState([]);
  const [selectedPart, setSelectedPart] = useState('');
  const [events, setEvents] = useState([]);
  const [prevClosing, setPrevClosing] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editData, setEditData] = useState({ qty: '', date: '', stage: '', stage_from: '', stage_to: '' });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/masters/items`);
      setItems(response.data);
    } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const fetchTraceability = useCallback(async () => {
    if (!selectedPart) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/part-traceability?part_id=${selectedPart}&month=${selectedMonth}`);
      setEvents(response.data.events || []);
      setPrevClosing(response.data.prev_closing || {});
      if ((response.data.events || []).length === 0) {
        toast.info('No transactions for this part in selected month');
      }
    } catch (err) { console.error(err);
      toast.error('Failed to fetch traceability data');
    } finally {
      setLoading(false);
    }
  }, [selectedPart, selectedMonth]);

  const startEdit = (idx, event) => {
    setEditingIdx(idx);
    const dateStr = event.date ? event.date.substring(0, 10) : '';
    let stage = '', stageFrom = '', stageTo = '';
    if (event.collection === 'stage_issue') {
      const parts = event.detail.split(' -> ');
      stageFrom = parts[0] || '';
      stageTo = (parts[1] || '').split(' (')[0] || '';
    } else if (event.collection === 'stage_receipt' || event.collection === 'opening_stock') {
      stage = event.balance_stage || '';
    }
    setEditData({
      qty: Math.abs(event.qty).toString(),
      date: dateStr,
      stage,
      stage_from: stageFrom,
      stage_to: stageTo
    });
  };

  const cancelEdit = () => {
    setEditingIdx(null);
    setEditData({ qty: '', date: '', stage: '', stage_from: '', stage_to: '' });
  };

  const saveEdit = async (event) => {
    try {
      const payload = { qty: parseInt(editData.qty) };
      if (editData.date) payload.date = editData.date + 'T00:00:00';
      if (event.collection === 'stage_issue') {
        if (editData.stage_from) payload.stage_from = editData.stage_from;
        if (editData.stage_to) payload.stage_to = editData.stage_to;
      } else if (event.collection === 'stage_receipt' || event.collection === 'opening_stock') {
        if (editData.stage) payload.stage = editData.stage;
      }
      await axios.put(
        `${API}/audit/fix-transaction/${event.collection}/${event.id}`,
        payload,
        {}
      );
      toast.success('Entry updated successfully');
      cancelEdit();
      fetchTraceability();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update entry');
    }
  };

  const getEventColor = (event) => {
    switch (event) {
      case 'Opening Stock': return 'bg-teal-50 text-teal-700 border-teal-200';
      case 'Cutting Production': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Stage Issue': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Stage Receipt': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Part Conversion (Out)': return 'bg-red-50 text-red-700 border-red-200';
      case 'Part Conversion (In)': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Dispatch': return 'bg-slate-100 text-slate-700 border-slate-300';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getTimelineColor = (event) => {
    switch (event) {
      case 'Opening Stock': return 'bg-teal-500';
      case 'Cutting Production': return 'bg-blue-500';
      case 'Stage Issue': return 'bg-amber-500';
      case 'Stage Receipt': return 'bg-emerald-500';
      case 'Part Conversion (Out)': return 'bg-red-500';
      case 'Part Conversion (In)': return 'bg-purple-500';
      case 'Dispatch': return 'bg-slate-600';
      default: return 'bg-gray-500';
    }
  };

  const canEditStage = (collection) => {
    return ['stage_issue', 'stage_receipt', 'opening_stock'].includes(collection);
  };

  const selectedItem = items.find(i => i.part_id === selectedPart);

  return (
    <div className="space-y-6" data-testid="part-traceability-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="part-traceability-title">PART TRACEABILITY</h1>
        <p className="text-sm text-text-muted mt-2">Trace a part's complete lifecycle from cutting to dispatch</p>
      </div>

      {/* Search Controls */}
      <div className="bg-white border border-border rounded-sm p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2 block">Select Part</label>
            <Select value={selectedPart} onValueChange={setSelectedPart} data-testid="part-select">
              <SelectTrigger data-testid="part-select-trigger" className="rounded-sm">
                <SelectValue placeholder="Choose a part..." />
              </SelectTrigger>
              <SelectContent>
                {items.map(item => (
                  <SelectItem key={item.part_id} value={item.part_id}>
                    {item.part_id} — {item.part_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2 block">Month</label>
            <Input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
              className="w-40 rounded-sm" data-testid="month-select" />
          </div>
          <Button
            data-testid="trace-button"
            onClick={fetchTraceability}
            disabled={loading || !selectedPart}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium"
          >
            <Search className="w-4 h-4 mr-2" />
            {loading ? 'LOADING...' : 'TRACE'}
          </Button>
        </div>

        {selectedItem && (
          <div className="mt-4 p-3 bg-gray-50 rounded-sm border border-border">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div><span className="text-text-muted">Part ID:</span> <span className="font-mono font-medium">{selectedItem.part_id}</span></div>
              <div><span className="text-text-muted">Name:</span> <span className="font-medium">{selectedItem.part_name}</span></div>
              <div><span className="text-text-muted">Steel Grade:</span> <span className="font-medium">{selectedItem.steel_grade}</span></div>
              <div><span className="text-text-muted">Process Flow:</span> <span className="font-medium">{selectedItem.std_process_flow}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      {events.length > 0 && (
        <div className="bg-white border border-border rounded-sm">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider">Transaction History ({events.length} events)</h2>
          </div>
          <div className="p-6">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

              {events.map((event, idx) => {
                const isEditing = editingIdx === idx;
                return (
                  <div key={`${event.id}-${idx}`} className={`relative flex gap-4 pb-6 last:pb-0 ${event.auto_correction ? 'bg-emerald-50/50 -mx-2 px-2 rounded-sm border border-emerald-200' : ''}`} data-testid={`trace-event-${idx}`}>
                    {/* Timeline dot */}
                    <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${getTimelineColor(event.event)}`}>
                      <span className="text-white text-xs font-bold">{idx + 1}</span>
                    </div>

                    {/* Event card */}
                    <div className="flex-1 pb-2">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getEventColor(event.event)}`}>
                          {event.event}
                        </span>
                        {event.auto_correction && (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 border-2 border-emerald-400 px-2.5 py-0.5 text-xs font-bold text-emerald-800 animate-pulse">
                            AUTO-CORRECTION
                          </span>
                        )}
                        {isEditing ? (
                          <Input
                            type="date"
                            value={editData.date}
                            onChange={(e) => setEditData(prev => ({ ...prev, date: e.target.value }))}
                            className="w-36 h-6 text-xs rounded-none"
                            data-testid={`edit-date-input-${idx}`}
                          />
                        ) : (
                          <span className="text-xs text-text-muted font-mono">
                            {new Date(event.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                      </div>

                      {/* Stage edit row for stage_issue */}
                      {isEditing && event.collection === 'stage_issue' ? (
                        <div className="flex items-center gap-2 mb-1">
                          <Select value={editData.stage_from} onValueChange={(v) => setEditData(prev => ({ ...prev, stage_from: v }))}>
                            <SelectTrigger className="w-28 h-6 text-xs rounded-none" data-testid={`edit-stage-from-${idx}`}>
                              <SelectValue placeholder="From" />
                            </SelectTrigger>
                            <SelectContent>
                              {STAGES.filter(s => s !== 'FG').map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="text-xs text-text-muted">-&gt;</span>
                          <Select value={editData.stage_to} onValueChange={(v) => setEditData(prev => ({ ...prev, stage_to: v }))}>
                            <SelectTrigger className="w-28 h-6 text-xs rounded-none" data-testid={`edit-stage-to-${idx}`}>
                              <SelectValue placeholder="To" />
                            </SelectTrigger>
                            <SelectContent>
                              {STAGES.map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : isEditing && canEditStage(event.collection) ? (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-text-muted">Stage:</span>
                          <Select value={editData.stage} onValueChange={(v) => setEditData(prev => ({ ...prev, stage: v }))}>
                            <SelectTrigger className="w-28 h-6 text-xs rounded-none" data-testid={`edit-stage-${idx}`}>
                              <SelectValue placeholder="Stage" />
                            </SelectTrigger>
                            <SelectContent>
                              {STAGES.map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <p className="text-sm text-text-secondary">{event.detail}</p>
                      )}

                      {event.correction_note && (
                        <p className="text-xs text-emerald-600 italic mt-0.5">{event.correction_note}</p>
                      )}
                      <div className="flex items-center gap-4 mt-1 text-xs text-text-muted">
                        <span>Qty:
                          {isEditing ? (
                            <span className="inline-flex items-center gap-1 ml-1">
                              <Input
                                type="number"
                                value={editData.qty}
                                onChange={(e) => setEditData(prev => ({ ...prev, qty: e.target.value }))}
                                className="w-20 h-6 text-xs text-right inline-block rounded-none"
                                data-testid={`edit-qty-input-${idx}`}
                                min="0"
                                autoFocus
                              />
                              <button onClick={() => saveEdit(event)} className="p-0.5 hover:bg-green-100 text-green-600 rounded-sm" data-testid={`save-edit-${idx}`}>
                                <Save className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={cancelEdit} className="p-0.5 hover:bg-red-100 text-red-500 rounded-sm" data-testid={`cancel-edit-${idx}`}>
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          ) : (
                            <span className={`font-mono font-semibold ml-1 ${event.qty < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                              {event.qty > 0 ? '+' : ''}{event.qty}
                            </span>
                          )}
                        </span>

                        {/* Balance indicator */}
                        {event.balance_after !== null && event.balance_after !== undefined && (
                          <span className="inline-flex items-center gap-1" data-testid={`balance-${idx}`}>
                            <span className="text-text-muted">{event.balance_stage}:</span>
                            <span className={`font-mono font-semibold ${event.balance_after < 0 ? 'text-red-600' : 'text-gray-800'}`}>
                              {event.balance_after}
                            </span>
                          </span>
                        )}

                        {event.created_by && <span>By: {event.created_by}</span>}
                        {isAdmin && !isEditing && event.id && (
                          <button
                            onClick={() => startEdit(idx, event)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            data-testid={`edit-btn-${idx}`}
                          >
                            <Edit2 className="w-3 h-3" />Edit
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {events.length === 0 && selectedPart && !loading && (
        <div className="bg-white border border-border rounded-sm p-12 text-center">
          <p className="text-gray-700">No transaction history found for this part. Select a part and click TRACE to view its lifecycle.</p>
        </div>
      )}
    </div>
  );
}
